import {
  createContext,
  use,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { createPublicClient, http, type Address } from 'viem';
import { useSendTransaction, useSwitchChain } from 'wagmi';
import { getConnection } from '@wagmi/core';
import { useQueryClient, type QueryClient } from '@tanstack/react-query';
import { track } from '@/shared/utils/track';
import { captureException } from '@/modules/capture-exception';
import { showNotification } from '@/modules/show-notification';
import { notifyApiError } from '@/modules/notify-api-error';
import { useStateReducer } from '@/modules/use-state-reducer';
import { getIsSendTransactionErrorUnexpected } from '@/modules/get-is-send-transaction-error-unexpected';
import Modal from '@/shared/ui/Modal';
import { wagmiConfig } from '@/shared/utils/wagmiConfig';
import { StoredAddressesSentTxs } from '@/shared/utils/Stored';
import { markTutorial } from '@/shared/utils/tutorials';
import { useProfile, type Profile } from '@/routes/Providers/ProfileProvider';
import type { Events } from '@/shared/utils/track';
import {
  createTxTracker,
  type CompletedContext,
  type MinedReceipt,
} from '@/modules/tx-tracker';
import {
  TX_KIND_EVENT,
  type TxKind,
  type TxDetails,
  type DetailsFor,
  type KindEventMap,
  type PendingTx,
} from '@/shared/utils/txKinds';
import {
  invalidateKindQueries,
  waitForStakeIndexed,
  waitForStakeWithdrawn,
  waitForSubscriptionSold,
} from './txCascade';
import ModalConfirmTxContent from './ModalConfirmTxContent';
import ModalSwitchNetworkContent from './ModalSwitchNetworkContent';

type SendTxParams = {
  network: { id: number };
  to?: Address;
  data?: `0x${string}`;
  value?: bigint;
};

type RawSendResult = {
  tx: { hash: `0x${string}`; data: string; value: unknown };
  networkIdHex: string;
};

type ExtensionContext<K extends TxKind> = {
  receipt: MinedReceipt;
  details: DetailsFor<K>;
};

// `status` is left out of trackProps because sendAndTrack injects it per
// lifecycle emission.
export type SendAndTrackArgs<K extends TxKind> = {
  kind: K;
  params: SendTxParams;
  modalHeader: string;
  details: DetailsFor<K>;
  trackProps: Omit<Events[KindEventMap[K]], 'status'>;
  onMined?: (ctx: ExtensionContext<K>) => Promise<void> | void;
  onReverted?: (ctx: ExtensionContext<K>) => Promise<void> | void;
};

type SendTxContextValue = {
  // Resolves once the wallet signed and the tracker took the tx over.
  // Rejects on a wallet rejection and on any other send failure alike, with
  // the failure already reported — analytics status, Sentry capture and the
  // error toast happen inside — so a caller's catch is control flow only
  // and never re-reports.
  sendAndTrack: <K extends TxKind>(args: SendAndTrackArgs<K>) => Promise<void>;
  isPending: (predicate: (tx: PendingTx) => boolean) => boolean;
  pending: readonly PendingTx[];
};

const SendTxContext = createContext<SendTxContextValue | null>(null);

export function useSendTx(): SendTxContextValue {
  const ctx = use(SendTxContext);
  if (!ctx)
    throw new Error('useSendTx must be used within SendTransactionProvider');
  return ctx;
}

type Extension = {
  onMined?: ((ctx: unknown) => Promise<void> | void) | undefined;
  onReverted?: ((ctx: unknown) => Promise<void> | void) | undefined;
};

type BaseCtx = {
  queryClient: QueryClient;
  updateProfile: () => Promise<Profile>;
  getProfile: () => Profile | null;
};

const createChainClient = (chain: (typeof wagmiConfig.chains)[number]) =>
  createPublicClient({ chain, transport: http() });
type ChainPublicClient = ReturnType<typeof createChainClient>;

export default function SendTransactionProvider({
  children,
}: {
  children: ReactNode;
}) {
  const { profile, updateProfile } = useProfile();
  const queryClient = useQueryClient();

  const sendTransactionAsync = useSendTransaction().mutateAsync;
  const switchChainAsync = useSwitchChain().mutateAsync;

  const txUiInitial: {
    modalSwitchToNetwork: false | { id: number };
    modalConfirmHeader: string | null;
  } = { modalSwitchToNetwork: false, modalConfirmHeader: null };
  const [{ modalConfirmHeader, modalSwitchToNetwork }, ss] =
    useStateReducer(txUiInitial);

  // One viem public client per chain, reused across receipt polls. Typed by
  // inference: zksync's chain formatters extend the transaction union, so the
  // default `PublicClient` parameterization can't hold these clients.
  const clientsRef = useRef(new Map<string, ChainPublicClient>());
  const getPublicClientFor = (networkIdHex: string): ChainPublicClient => {
    const cached = clientsRef.current.get(networkIdHex);
    if (cached) return cached;
    const chainId = parseInt(networkIdHex, 16);
    const chain = wagmiConfig.chains.find((c) => c.id === chainId);
    if (!chain) throw new Error(`Unknown chain: ${networkIdHex}`);
    const client = createChainClient(chain);
    clientsRef.current.set(networkIdHex, client);
    return client;
  };

  // Latest values referenced by the tracker's completion callback. The
  // tracker outlives individual renders, so it reads via a ref updated
  // post-commit — completion callbacks fire from async receipt polls, never
  // inside the render→effect window, so the ref is always current for them.
  const baseCtxRef = useRef<BaseCtx>({
    queryClient,
    updateProfile,
    getProfile: () => profile,
  });
  useEffect(() => {
    baseCtxRef.current = {
      queryClient,
      updateProfile,
      getProfile: () => profile,
    };
  });

  // In-memory only — a reload before the tx mines runs only the base
  // handler for that kind.
  const extensionsRef = useRef(new Map<string, Extension>());

  // Everything the closure captures is module-scope or a stable ref, so
  // the once-created tracker never goes stale.
  const [tracker] = useState(() =>
    createTxTracker({
      getPublicClient: (networkIdHex) => {
        const client = getPublicClientFor(networkIdHex);
        return {
          waitForTransactionReceipt: (args) =>
            client.waitForTransactionReceipt(args),
        };
      },
      storage: {
        get: (a) => StoredAddressesSentTxs.get(a),
        set: (a, s) => StoredAddressesSentTxs.set(a, s),
      },
      onCompleted: (ctx) =>
        handleCompleted(ctx, baseCtxRef.current, extensionsRef.current),
      onTrack: (event, props) =>
        (track as (e: string, p: Record<string, unknown>) => void)(
          event,
          props,
        ),
    }),
  );

  useEffect(() => {
    tracker.setAddress(profile?.address ?? null);
  }, [profile?.address, tracker]);

  const pending = useSyncExternalStore(
    (listener) => tracker.subscribe(listener),
    () => tracker.getPending(),
    () => tracker.getPending(),
  );

  // eslint-disable-next-line no-restricted-syntax -- the compiler skips this component (a throw inside try/catch in switchTo, rawSend and sendAndTrack), so this memo still carries its render-skipping
  const isPending = useCallback(
    (predicate: (tx: PendingTx) => boolean) => pending.some(predicate),
    [pending],
  );

  // eslint-disable-next-line no-restricted-syntax -- the compiler skips this component (a throw inside try/catch in switchTo, rawSend and sendAndTrack), so this memo still carries its render-skipping
  const switchTo = useCallback(
    async (network: { id: number }) => {
      try {
        try {
          ss({ modalSwitchToNetwork: network });
          await switchChainAsync({ chainId: network.id });
          ss({ modalSwitchToNetwork: false });
          track('network_switch', { status: 'signed' });
        } catch (e) {
          ss({ modalSwitchToNetwork: false });
          const err = e as { message?: string };
          if (
            err.message?.includes('JSON RPC response format is invalid') !==
            true
          ) {
            throw e;
          }
          // Quirky wallets DO switch but return a malformed response — the
          // swallow treats it as success, so it must count as signed too.
          track('network_switch', { status: 'signed' });
        }
      } catch (e) {
        if (!getIsSendTransactionErrorUnexpected(e)) {
          track('network_switch', { status: 'rejected' });
          return { canceled: true as const };
        }
        const err = e as { message?: string };
        if (
          err.message?.includes(
            "'wallet_switchEthereumChain' already pending",
          ) === true
        ) {
          track('network_switch', { status: 'error' });
          showNotification(
            'error',
            'Switching network already pending. Check your MetaMask.',
          );
          return { canceled: true as const };
        }
        track('network_switch', { status: 'error' });
        captureException(e);
        throw e;
      }
      return undefined;
    },
    [ss, switchChainAsync],
  );

  // eslint-disable-next-line no-restricted-syntax -- the compiler skips this component (a throw inside try/catch in switchTo, rawSend and sendAndTrack), so this memo still carries its render-skipping
  const rawSend = useCallback(
    async ({
      params: { network, ...params },
      modalHeader,
    }: {
      params: SendTxParams;
      modalHeader: string;
    }): Promise<RawSendResult> => {
      try {
        ss({ modalConfirmHeader: modalHeader });

        // The connector's provider, not wagmi's tracked chainId
        // (publicClient.getChainId() / getChainId(wagmiConfig)): that one
        // silently falls back to a config chain when the wallet is on one not
        // in wagmiConfig.chains (e.g. Arbitrum), which makes the precheck pass
        // and lets sendTransaction fail with ChainMismatchError.
        const connector = getConnection(wagmiConfig).connector;
        const provider = (await connector?.getProvider()) as
          | { request: (args: { method: string }) => Promise<unknown> }
          | undefined;
        const chainHex = (await provider?.request({
          method: 'eth_chainId',
        })) as string | undefined;
        const walletChainId =
          chainHex !== undefined ? parseInt(chainHex, 16) : undefined;
        if (walletChainId !== undefined && walletChainId !== network.id) {
          const switchResult = await switchTo(network);
          if (switchResult?.canceled) throw new Error('cancelled');
        }

        const hash = await sendTransactionAsync({
          chainId: network.id,
          ...(params as Record<string, unknown>),
        });

        ss({ modalConfirmHeader: null });

        return {
          tx: {
            hash,
            data: (params as { data?: string }).data ?? '0x',
            value: (params as { value?: unknown }).value,
          },
          networkIdHex: `0x${network.id.toString(16)}`,
        };
      } catch (e) {
        ss({ modalConfirmHeader: null });
        throw e;
      }
    },
    [sendTransactionAsync, ss, switchTo],
  );

  // eslint-disable-next-line no-restricted-syntax -- the compiler skips this component (a throw inside try/catch in switchTo, rawSend and sendAndTrack), so this memo still carries its render-skipping
  const sendAndTrack = useCallback(
    async <K extends TxKind>(args: SendAndTrackArgs<K>): Promise<void> => {
      const event = TX_KIND_EVENT[args.kind];
      const baseProps = args.trackProps as Record<string, unknown>;
      // track()'s typed overloads don't resolve against a generic `keyof
      // Events` + dynamic props, so bypass them for the internal dispatch.
      const trackLoose = track as (
        e: string,
        p: Record<string, unknown>,
      ) => void;

      let result: RawSendResult;
      try {
        result = await rawSend({
          params: args.params,
          modalHeader: args.modalHeader,
        });
        trackLoose(event, { ...baseProps, status: 'signed' });
      } catch (e) {
        if (!getIsSendTransactionErrorUnexpected(e)) {
          trackLoose(event, { ...baseProps, status: 'rejected' });
        } else {
          trackLoose(event, { ...baseProps, status: 'error' });
          captureException(e);
          notifyApiError(e);
        }
        throw e;
      }

      if (args.onMined || args.onReverted) {
        extensionsRef.current.set(result.tx.hash, {
          onMined: args.onMined as Extension['onMined'],
          onReverted: args.onReverted as Extension['onReverted'],
        });
      }
      tracker.submit({
        hash: result.tx.hash,
        networkIdHex: result.networkIdHex,
        details: args.details,
        event,
        trackProps: args.trackProps,
      });
    },
    [rawSend, tracker],
  );

  // eslint-disable-next-line no-restricted-syntax -- the compiler skips this component (a throw inside try/catch in switchTo, rawSend and sendAndTrack), so this memo still carries its render-skipping
  const value = useMemo<SendTxContextValue>(
    () => ({ sendAndTrack, isPending, pending }),
    [sendAndTrack, isPending, pending],
  );

  return (
    <>
      {modalConfirmHeader != null && modalConfirmHeader !== '' && (
        <Modal>
          <ModalConfirmTxContent header={modalConfirmHeader} />
        </Modal>
      )}
      {modalSwitchToNetwork !== false && (
        <Modal>
          <ModalSwitchNetworkContent network={modalSwitchToNetwork} />
        </Modal>
      )}
      <SendTxContext value={value}>{children}</SendTxContext>
    </>
  );
}

// The base handler runs for every tx of its kind — freshly submitted OR
// reloaded from localStorage after a refresh — and owns the "always
// required" side of the flow (query invalidations, profile refresh,
// notifications); caller-specific UI (share modals, form resets) goes in
// the sendAndTrack extension, which runs after it.
async function handleCompleted(
  ctx: CompletedContext<keyof Events, TxDetails>,
  base: BaseCtx,
  extensions: Map<string, Extension>,
) {
  let extraExt: Record<string, unknown> = {};
  try {
    extraExt = await runBaseHandler(ctx, base);
  } catch (e) {
    captureException(e);
  }

  const ext = extensions.get(ctx.tx.hash);
  if (!ext) return;
  extensions.delete(ctx.tx.hash);
  try {
    const extensionCtx = {
      receipt: ctx.receipt,
      details: ctx.tx.details,
      ...extraExt,
    };
    if (ctx.status === 'mined' && ext.onMined) await ext.onMined(extensionCtx);
    if (ctx.status === 'reverted' && ext.onReverted)
      await ext.onReverted(extensionCtx);
  } catch (e) {
    captureException(e);
  }
}

async function runBaseHandler(
  ctx: CompletedContext<keyof Events, TxDetails>,
  base: BaseCtx,
): Promise<Record<string, unknown>> {
  const { tx, status } = ctx;
  switch (tx.details.kind) {
    case 'sell_subscription': {
      if (status === 'reverted') {
        showNotification('error', 'Something went wrong, try again');
        return {};
      }
      await waitForSubscriptionSold(
        tx.details,
        base.getProfile()?.address ?? null,
      );
      await base.updateProfile();
      showNotification('info', 'Key(s) sold');
      return {};
    }

    case 'claim_san':
    case 'claim_points':
    case 'claim_legacy_san': {
      if (status === 'reverted') {
        showNotification('error', 'SAN claim failed, try again');
        return {};
      }
      await invalidateKindQueries(base.queryClient, tx.details.kind, 'mined');
      showNotification('info', 'SAN claimed');
      return {};
    }

    case 'withdraw_zk_fees': {
      if (status === 'reverted') {
        showNotification('error', 'Withdrawal failed, try again');
        return {};
      }
      await invalidateKindQueries(base.queryClient, tx.details.kind, 'mined');
      showNotification('info', 'Withdrawal completed');
      return {};
    }

    case 'stake_approve': {
      if (status === 'reverted') {
        showNotification('error', 'SAN approve failed, try again');
        return {};
      }
      await invalidateKindQueries(base.queryClient, tx.details.kind, 'mined');
      showNotification('info', 'SAN approved');
      return {};
    }

    case 'stake_deposit': {
      if (status === 'reverted') {
        showNotification('error', 'Backing failed, try again');
        return {};
      }
      // Session-deduped and idempotent — safe on reloaded-from-storage mines.
      void markTutorial('first_stake');
      await invalidateKindQueries(base.queryClient, tx.details.kind, 'mined');
      await waitForStakeIndexed(tx.details);
      await invalidateKindQueries(base.queryClient, tx.details.kind, 'indexed');
      showNotification('info', 'Backing successful');
      return {};
    }

    case 'stake_withdraw': {
      if (status === 'reverted') {
        showNotification('error', 'Backing withdrawal failed, try again');
        return {};
      }
      await invalidateKindQueries(base.queryClient, tx.details.kind, 'mined');
      // Punished stakes never move active_stake_on_user — poll the signal
      // that DOES move for them instead (the withdrawn row dropping out of
      // /v1/stakes) so the indexed-phase refetch doesn't race the indexer.
      if (tx.details.stakeInactive === true) {
        await waitForStakeWithdrawn(tx.details);
      } else {
        await waitForStakeIndexed(tx.details);
      }
      await invalidateKindQueries(base.queryClient, tx.details.kind, 'indexed');
      showNotification('info', 'Backing withdrawn');
      return {};
    }
  }
}
